import React, { useState, useEffect } from 'react';
import { format, addDays, subDays } from 'date-fns';
import { Calendar, Users, Settings, ChevronLeft, ChevronRight, Filter, Search, Map, Clock, CheckCircle, AlertCircle, PlayCircle, Car } from 'lucide-react';
import axios from 'axios';
import LiveMap from './LiveMap';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TwoColumnWorkPlanning = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, planned, completed
  const [peopleFilter, setPeopleFilter] = useState('');
  const [allPeople, setAllPeople] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview, active, planned, completed
  const [currentTime, setCurrentTime] = useState(new Date());

  // Live time update every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  // Mark task as completed

  // Load initial data
  useEffect(() => {
    loadProjects();
    loadPeople();
  }, []);

  // Load project data when project/date changes
  useEffect(() => {
    if (selectedProject && selectedDate) {
      loadTaskAssignments();
    }
  }, [selectedProject, selectedDate]);

  const loadProjects = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };
      const response = await axios.get(`${API_BASE_URL}/api/projects/`, { headers });
      setProjects(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading projects:', error);
      setLoading(false);
    }
  };

  const loadPeople = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };
      const [usersResponse, carsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/users/`, { headers }),
        axios.get(`${API_BASE_URL}/api/cars/`, { headers })
      ]);
      setAllPeople(usersResponse.data);
      setAllCars(carsResponse.data);
    } catch (error) {
      console.error('Error loading people and cars:', error);
    }
  };

  const loadTaskAssignments = async () => {
    if (!selectedProject || !selectedDate) return;

    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      };

      const dateString = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_BASE_URL}/api/task-assignments/?project=${selectedProject}&date=${dateString}`,
        { headers }
      );

      setTaskAssignments(response.data);
    } catch (error) {
      console.error('Error loading task assignments:', error);
    }
  };

  const handleDateChange = (direction) => {
    if (direction === 'prev') {
      setSelectedDate(prev => subDays(prev, 1));
    } else {
      setSelectedDate(prev => addDays(prev, 1));
    }
  };

  // Get current time for live status
  const currentTimeStr = format(currentTime, 'HH:mm');

  // Filter and categorize tasks
  const getFilteredTasks = () => {
    return taskAssignments.filter(task => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesTask = task.subcategory?.toLowerCase().includes(searchLower) ||
                           task.description?.toLowerCase().includes(searchLower);
        const matchesTurbine = `T${task.turbine_id}`.toLowerCase().includes(searchLower);

        if (!matchesTask && !matchesTurbine) return false;
      }

      // People filter
      if (peopleFilter && task.assigned_people && task.assigned_people.length > 0) {
        const hasMatchingPerson = task.assigned_people.some(personId => {
          const person = allPeople.find(p => p.id === personId);
          if (!person) return false;
          const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
          return fullName.includes(peopleFilter.toLowerCase());
        });
        if (!hasMatchingPerson) return false;
      }

      return true;
    });
  };

  const getTaskStatus = (task) => {
    const isActive = currentTimeStr >= task.start_time && currentTimeStr <= task.end_time;
    const isPast = currentTimeStr > task.end_time;

    // Debug log for active tasks
    if (isActive) {
      console.log('🟢 ACTIVE TASK:', {
        turbineId: task.turbine_id,
        subcategory: task.subcategory,
        currentTime: currentTimeStr,
        startTime: task.start_time,
        endTime: task.end_time,
        assignedCar: task.assigned_car
      });
    }

    if (isPast) return 'completed';
    if (isActive) return 'active';
    return 'planned';
  };

  const categorizedTasks = {
    all: getFilteredTasks(),
    active: getFilteredTasks().filter(task => getTaskStatus(task) === 'active'),
    planned: getFilteredTasks().filter(task => getTaskStatus(task) === 'planned'),
    completed: getFilteredTasks().filter(task => getTaskStatus(task) === 'completed')
  };

  const getPersonName = (personId) => {
    const person = allPeople.find(p => p.id === personId);
    if (!person) return `User ${personId}`;
    return person.first_name && person.last_name
      ? `${person.first_name} ${person.last_name}`
      : person.username;
  };

  const getCarInfo = (carId) => {
    const car = allCars.find(c => c.id === carId);
    if (!car) return null;
    return `${car.car_brand} ${car.car_model} (${car.plate_number})`;
  };

  const getRemainingTime = (endTime) => {
    const [endHour, endMinute] = endTime.split(':');
    const endTimeDate = new Date(currentTime);
    endTimeDate.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

    const diff = endTimeDate - currentTime;
    const remainingMinutes = Math.max(0, Math.ceil(diff / (1000 * 60)));

    if (remainingMinutes === 0) return 'Finishing!';
    if (remainingMinutes < 60) return `${remainingMinutes}min left`;
    const hours = Math.floor(remainingMinutes / 60);
    const mins = remainingMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}min left` : `${hours}h left`;
  };

  const getElapsedTime = (startTime) => {
    const [startHour, startMinute] = startTime.split(':');
    const startTimeDate = new Date(currentTime);
    startTimeDate.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

    const diff = currentTime - startTimeDate;
    const elapsedMinutes = Math.max(0, Math.floor(diff / (1000 * 60)));

    if (elapsedMinutes < 60) return `${elapsedMinutes}min ago`;
    const hours = Math.floor(elapsedMinutes / 60);
    const mins = elapsedMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}min ago` : `${hours}h ago`;
  };

  const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Map className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Work Planning</h1>
              <p className="text-sm text-gray-600">
                Visual task management with live map • 🔴 Live: {format(currentTime, 'HH:mm:ss')}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {/* Date Navigation */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleDateChange('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
                <Calendar className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">
                  {format(selectedDate, 'EEE, MMM d, yyyy')}
                </span>
              </div>
              <button
                onClick={() => handleDateChange('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Project Selection */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Two Columns */}
      <div className="flex h-[calc(100vh-120px)]">
        {/* Left Column - Map */}
        <div className="w-2/3 border-r border-gray-200 bg-white">
          {selectedProject ? (
            <LiveMap
              selectedProject={selectedProject}
              selectedDate={selectedDate}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center py-8">
                <Map className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <div className="text-lg font-medium text-gray-700 mb-2">
                  Select a project to view map
                </div>
                <div className="text-sm text-gray-500">
                  Choose a project from the dropdown above to see turbine locations and task status
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Tasks */}
        <div className="w-1/3 bg-white flex flex-col">
          {/* Task Filters and Controls */}
          <div className="p-4 border-b border-gray-200">
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tasks or turbines..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* People Filter */}
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Filter by person name..."
                  value={peopleFilter}
                  onChange={(e) => setPeopleFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Status Tabs */}
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'overview', label: 'Overview', icon: Filter },
                  { key: 'active', label: 'Active', icon: PlayCircle },
                  { key: 'planned', label: 'Planned', icon: Clock },
                  { key: 'completed', label: 'Done', icon: CheckCircle }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                      activeTab === tab.key
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <tab.icon className="w-3 h-3" />
                    <span>{tab.label}</span>
                    <span className="text-xs bg-gray-200 text-gray-600 px-1 rounded">
                      {categorizedTasks[tab.key === 'overview' ? 'all' : tab.key].length}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          {selectedProject && (
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>📊 Total: {categorizedTasks.all.length} tasks</span>
                <span>🟢 {categorizedTasks.active.length} active • 🟡 {categorizedTasks.planned.length} planned • 🔵 {categorizedTasks.completed.length} done</span>
              </div>
            </div>
          )}

          {/* Task List */}
          <div className="flex-1 overflow-y-auto">
            {selectedProject ? (
              <div className="p-4">
                {activeTab === 'overview' ? (
                  // Overview - Show summary cards
                  <div className="space-y-4">
                    {/* Summary Stats - Compact Grid */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                        <PlayCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-green-900">{categorizedTasks.active.length}</div>
                        <div className="text-xs text-green-700">Active</div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 text-center">
                        <Clock className="w-4 h-4 text-yellow-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-yellow-900">{categorizedTasks.planned.length}</div>
                        <div className="text-xs text-yellow-700">Planned</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                        <CheckCircle className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                        <div className="text-lg font-bold text-blue-900">{categorizedTasks.completed.length}</div>
                        <div className="text-xs text-blue-700">Done</div>
                      </div>
                    </div>

                    {/* Live Active Tasks - Enhanced with Time Info */}
                    {categorizedTasks.active.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            🔴 LIVE Active Tasks ({categorizedTasks.active.length})
                          </h3>
                          <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {format(currentTime, 'HH:mm:ss')} | {format(selectedDate, 'dd.MM.yyyy')}
                          </div>
                        </div>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                          {categorizedTasks.active.map(task => {
                            const carInfo = task.assigned_car ? getCarInfo(task.assigned_car) : null;
                            const peopleNames = task.assigned_people?.map(id => getPersonName(id)) || [];
                            const remainingTime = getRemainingTime(task.end_time);
                            const elapsedTime = getElapsedTime(task.start_time);

                            return (
                              <div key={task.id} className="bg-green-50 border-l-4 border-green-500 rounded-r p-3 hover:bg-green-100 transition-colors shadow-sm">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center space-x-2">
                                        <div className="font-bold text-green-900 text-sm">
                                          🌪️ T{task.turbine_id}
                                        </div>
                                        <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
                                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded font-medium">
                                          ACTIVE
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2 text-xs">
                                        <span className="text-gray-600">⏰ {task.start_time} - {task.end_time}</span>
                                        <span className="text-green-600 font-bold bg-green-100 px-2 py-1 rounded">{remainingTime}</span>
                                      </div>
                                    </div>

                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-sm font-medium text-green-800">
                                        {task.category ? `${task.category} - ${task.subcategory}` : task.subcategory}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        Started {elapsedTime}
                                      </div>
                                    </div>

                                    {peopleNames.length > 0 && (
                                      <div className="text-xs text-green-700 mb-1">
                                        👥 {peopleNames.length > 2
                                          ? `${peopleNames.slice(0, 2).join(', ')} +${peopleNames.length - 2} more`
                                          : peopleNames.join(', ')
                                        }
                                      </div>
                                    )}

                                    {carInfo && (
                                      <div className="flex items-center space-x-1 text-xs text-green-700">
                                        <Car className="w-3 h-3" />
                                        <span className="truncate">{carInfo}</span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="ml-2 flex flex-col items-center">
                                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mb-1"></div>
                                    <div className="text-xs font-bold text-green-600">LIVE</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Upcoming Tasks - Enhanced List */}
                    {categorizedTasks.planned.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                          <Clock className="w-3 h-3 text-yellow-600 mr-2" />
                          🟡 Next Up ({categorizedTasks.planned.slice(0, 3).length} of {categorizedTasks.planned.length})
                        </h3>
                        <div className="space-y-2">
                          {categorizedTasks.planned.slice(0, 3).map(task => {
                            const carInfo = task.assigned_car ? getCarInfo(task.assigned_car) : null;
                            const peopleNames = task.assigned_people?.map(id => getPersonName(id)) || [];

                            return (
                              <div key={task.id} className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r p-2 hover:bg-yellow-100 transition-colors shadow-sm">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <div className="font-bold text-yellow-900 text-sm">
                                        🌪️ T{task.turbine_id}
                                      </div>
                                      <div className="text-yellow-600 font-medium text-xs">
                                        starts {task.start_time}
                                      </div>
                                    </div>

                                    <div className="text-sm font-medium text-yellow-800 mb-1">
                                      {task.category ? `${task.category} - ${task.subcategory}` : task.subcategory}
                                    </div>

                                    {peopleNames.length > 0 && (
                                      <div className="text-xs text-yellow-700 mb-1">
                                        👥 {peopleNames.length > 2
                                          ? `${peopleNames.slice(0, 2).join(', ')} +${peopleNames.length - 2} more`
                                          : peopleNames.join(', ')
                                        }
                                      </div>
                                    )}

                                    {carInfo && (
                                      <div className="flex items-center space-x-1 text-xs text-yellow-700">
                                        <Car className="w-3 h-3" />
                                        <span className="truncate">{carInfo}</span>
                                      </div>
                                    )}
                                  </div>

                                  <div className="ml-2 flex flex-col items-center">
                                    <Clock className="w-3 h-3 text-yellow-600 mb-1" />
                                    <div className="text-xs font-medium text-yellow-600">PLANNED</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {categorizedTasks.planned.length > 3 && (
                            <div className="text-center py-1">
                              <button
                                onClick={() => setActiveTab('planned')}
                                className="text-xs text-yellow-600 hover:text-yellow-800 font-medium"
                              >
                                +{categorizedTasks.planned.length - 3} more tasks...
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Recent Completed - Enhanced */}
                    {categorizedTasks.completed.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                          <CheckCircle className="w-3 h-3 text-blue-600 mr-2" />
                          🔵 Recently Completed ({categorizedTasks.completed.length})
                        </h3>
                        <div className="space-y-2">
                          {categorizedTasks.completed.slice(0, 2).map(task => {
                            const carInfo = task.assigned_car ? getCarInfo(task.assigned_car) : null;
                            const peopleNames = task.assigned_people?.map(id => getPersonName(id)) || [];

                            return (
                              <div key={task.id} className={`${task.is_completed ? 'bg-gray-50 border-l-4 border-gray-400 opacity-75' : 'bg-blue-50 border-l-4 border-blue-400'} rounded-r p-2 hover:bg-blue-100 transition-colors shadow-sm`}>
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <div className="font-bold text-blue-900 text-sm">
                                        🌪️ T{task.turbine_id}
                                      </div>
                                      <div className="text-blue-600 font-medium text-xs">
                                        ended {task.end_time}
                                      </div>
                                    </div>

                                    <div className="text-sm font-medium text-blue-800 mb-1">
                                      {task.category ? `${task.category} - ${task.subcategory}` : task.subcategory}
                                    </div>

                                    {peopleNames.length > 0 && (
                                      <div className="text-xs text-blue-700 mb-1">
                                        👤 Completed by: {peopleNames.length > 2
                                          ? `${peopleNames.slice(0, 2).join(', ')} +${peopleNames.length - 2} more`
                                          : peopleNames.join(', ')
                                        }
                                      </div>
                                    )}

                                    {carInfo && (
                                      <div className="flex items-center space-x-1 text-xs text-blue-700">
                                        <Car className="w-3 h-3" />
                                        <span className="truncate">{carInfo}</span>
                                      </div>
                                    )}
                                  </div>

                                </div>
                              </div>
                            );
                          })}
                          {categorizedTasks.completed.length > 2 && (
                            <div className="text-center py-1">
                              <button
                                onClick={() => setActiveTab('completed')}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                View all {categorizedTasks.completed.length} completed...
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Detailed task list for specific status
                  <div className="space-y-2">
                    {categorizedTasks[activeTab].length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <div className="text-sm">No {activeTab} tasks found</div>
                        <div className="text-xs text-gray-400 mt-1">
                          {searchTerm || peopleFilter ? 'Try adjusting your filters' : `No tasks in ${activeTab} status`}
                        </div>
                      </div>
                    ) : (
                      categorizedTasks[activeTab].map(task => {
                        const status = getTaskStatus(task);
                        const statusConfig = {
                          active: { color: 'green', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800', iconColor: 'text-green-600' },
                          planned: { color: 'yellow', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', textColor: 'text-yellow-800', iconColor: 'text-yellow-600' },
                          completed: { color: 'blue', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-800', iconColor: 'text-blue-600' }
                        };
                        const config = statusConfig[status];

                        return (
                          <div key={task.id} className={`${task.is_completed ? 'bg-gray-50 border-gray-400 opacity-75' : config.bgColor + ' ' + config.borderColor} border rounded-lg p-2 hover:shadow-sm transition-shadow`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate">
                                  T{task.turbine_id}: {task.category ? `${task.category} - ${task.subcategory}` : task.subcategory}
                                </div>
                                <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                                  <span>⏰ {task.start_time} - {task.end_time}</span>
                                  {task.assigned_people && task.assigned_people.length > 0 && (
                                    <span>👥 {task.assigned_people.length} people</span>
                                  )}
                                </div>
                                {task.assigned_people && task.assigned_people.length > 0 && (
                                  <div className="mt-1 text-xs text-gray-600 truncate">
                                    {task.assigned_people.map(personId => getPersonName(personId)).join(', ')}
                                  </div>
                                )}
                              </div>
                              <div className="ml-2 flex items-center space-x-2">
                                {status === 'active' && (
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-sm">Select a project to view tasks</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoColumnWorkPlanning;